/* =========================================================================
   Galaxy mobile image menu: sale banner                          2026-10-12
   Puts the homepage banner's sale slide (phone version) into the mobile drawer,
   so every campaign shows up in the menu automatically, whatever the banner's
   design. Markup slot: [data-gx-mm-banner] in snippets/galaxy-mobile-menu.liquid.

   - Runs only on phones (< 750px) and only when the drawer is opened (starts on
     the first touch of the hamburger to win a few hundred ms). Nothing is
     fetched on page load.
   - On the homepage the slide is copied straight from the page: no request.
   - Elsewhere it comes from Shopify's Section Rendering API. That API needs the
     banner's full section id, "template--<number>__galaxy_banner", and the
     number changes with every theme copy, so it is looked up, not hard-coded:
       1. read from the page when on the homepage, else
       2. localStorage (per theme id; saved on any earlier homepage visit), else
       3. one streamed read of the homepage, stopped as soon as the id appears.
     The slide HTML is then cached for 10 min per tab (sessionStorage).
   - Which slide: the one with "Show in mobile menu" ticked (data-gx-menu); else
     the one that links to the sale collection (data-sale-url, from the menu).
     None found, or anything fails -> the red text card stays, no gap.
   - Phone image only: the desktop image / video is removed before insertion
     (a display:none <img> still downloads), videos become their poster image.
   ========================================================================= */
(function () {
  'use strict';
  var CACHE_MS = 10 * 60 * 1000;
  var TIMEOUT_MS = 8000;
  var ID_RE = /shopify-section-(template--\d+__galaxy_banner)/;
  var started = false;

  function isPhone() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }
  function theme() {
    return (window.Shopify && window.Shopify.theme) || null;
  }
  function themeKey() {
    var t = theme();
    return t && t.id ? String(t.id) : '';
  }
  function withPreview(u) {
    var t = theme();
    if (t && t.id && t.role && t.role !== 'main') u += (u.indexOf('?') === -1 ? '?' : '&') + 'preview_theme_id=' + t.id;
    return u;
  }
  function rootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function store(kind) {
    try { return window[kind]; } catch (e) { return null; }
  }
  function get(kind, k) {
    var s = store(kind);
    try { return s ? s.getItem(k) : null; } catch (e) { return null; }
  }
  function set(kind, k, v) {
    var s = store(kind);
    try { if (s) { if (v == null) s.removeItem(k); else s.setItem(k, v); } } catch (e) {}
  }
  function pageBannerSection() {
    return document.querySelector('[id^="shopify-section-template--"][id$="__galaxy_banner"]');
  }
  function withTimeout(p) {
    return Promise.race([p, new Promise(function (_, reject) {
      setTimeout(function () { reject(new Error('timeout')); }, TIMEOUT_MS);
    })]);
  }

  /* --- the banner section id --- */
  function idKey() { return 'gxMmBannerId:' + themeKey(); }
  function discoverId() {
    return fetch(withPreview(rootPath()), { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('status ' + res.status);
      if (!res.body || !res.body.getReader || !window.TextDecoder) {
        return res.text().then(function (t) { var m = ID_RE.exec(t); return m ? m[1] : null; });
      }
      var reader = res.body.getReader();
      var dec = new TextDecoder();
      var tail = '';
      var seen = 0;
      function pump() {
        return reader.read().then(function (r) {
          if (r.done) return null;
          var chunk = tail + dec.decode(r.value, { stream: true });
          seen += r.value.length;
          var m = ID_RE.exec(chunk);
          if (m) { try { reader.cancel(); } catch (e) {} return m[1]; }
          if (seen > 2000000) { try { reader.cancel(); } catch (e) {} return null; }
          tail = chunk.slice(-200);
          return pump();
        });
      }
      return pump();
    }).then(function (id) {
      if (!id) throw new Error('no banner on homepage');
      set('localStorage', idKey(), id);
      return id;
    });
  }
  function getId() {
    var cached = get('localStorage', idKey());
    return cached ? Promise.resolve(cached) : discoverId();
  }

  /* --- the banner section HTML (not on the homepage) --- */
  function htmlKey() { return 'gxMmBanner:' + themeKey(); }
  function fetchSection(id) {
    return fetch(withPreview(rootPath() + '?sections=' + encodeURIComponent(id)), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
      .then(function (j) { return j ? j[id] : null; });
  }
  function getHtml() {
    try {
      var c = JSON.parse(get('sessionStorage', htmlKey()) || 'null');
      if (c && c.h && Date.now() - c.t < CACHE_MS) return Promise.resolve(c.h);
    } catch (e) {}
    return getId().then(function (id) {
      return fetchSection(id).then(function (h) {
        if (h) return h;
        /* the stored id is stale (theme or template changed): look it up again once */
        set('localStorage', idKey(), null);
        return discoverId().then(fetchSection);
      });
    }).then(function (h) {
      if (!h) throw new Error('no banner section');
      set('sessionStorage', htmlKey(), JSON.stringify({ t: Date.now(), h: h }));
      return h;
    });
  }

  /* --- build --- */
  /* Phone-sized request for plain CDN URLs (image_url settings have no srcset). */
  function sized(src) {
    if (!src || !/cdn\.shopify\.com|\/cdn\/shop\//.test(src) || /[?&]width=/.test(src)) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=828';
  }
  function pickSlide(root, saleUrl) {
    var ticked = root.querySelector('.gb-slide[data-gx-menu]');
    if (ticked) return ticked;
    if (!saleUrl) return null;
    var slides = root.querySelectorAll('.gb-slide');
    for (var i = 0; i < slides.length; i++) {
      var links = slides[i].querySelectorAll('a[href]');
      for (var k = 0; k < links.length; k++) {
        if ((links[k].getAttribute('href') || '').indexOf(saleUrl) !== -1) return slides[i];
      }
    }
    return null;
  }
  function build(root, styles, slot) {
    var picked = pickSlide(root, slot.getAttribute('data-sale-url'));
    if (!picked) return false;

    var slide = document.importNode(picked, true);
    var hasMobile = slide.classList.contains('has-mobile');
    slide.classList.add('is-active');
    slide.removeAttribute('aria-hidden');
    slide.removeAttribute('aria-roledescription');
    slide.removeAttribute('aria-label');
    slide.removeAttribute('data-shopify-editor-block');

    if (hasMobile) {
      Array.prototype.forEach.call(slide.querySelectorAll('.gb-img--desktop, .gb-backdrop--desktop'), function (n) { n.remove(); });
    }
    Array.prototype.forEach.call(slide.querySelectorAll('video'), function (v) {
      var poster = v.getAttribute('poster');
      if (!poster) { v.remove(); return; }
      var img = document.createElement('img');
      img.className = v.className.replace(/\bgb-video\b/, '').trim();
      img.alt = v.getAttribute('aria-label') || '';
      img.src = sized(poster);
      v.parentNode.replaceChild(img, v);
    });
    Array.prototype.forEach.call(slide.querySelectorAll('img'), function (img) {
      if (!img.hasAttribute('srcset')) img.setAttribute('src', sized(img.getAttribute('src')));
      img.setAttribute('loading', 'eager');
    });
    Array.prototype.forEach.call(slide.querySelectorAll('script'), function (s) { s.remove(); });

    /* The section's CSS is scoped to #GalaxyBanner-<section id>, so the wrapper
       reuses that id. */
    (styles || []).forEach(function (s) { slot.appendChild(document.importNode(s, true)); });
    var wrap = document.createElement('div');
    wrap.id = root.id;
    wrap.className = 'galaxy-banner gb-single gx-mm-banner';
    var viewport = document.createElement('div');
    viewport.className = 'gb-viewport';
    viewport.appendChild(slide);
    wrap.appendChild(viewport);
    slot.appendChild(wrap);
    slot.setAttribute('data-state', 'ready');
    return true;
  }
  function fail(slot) { slot.setAttribute('data-state', 'fallback'); }

  function start() {
    if (started) return;
    var slot = document.querySelector('[data-gx-mm-banner]');
    if (!slot || !isPhone()) return;
    started = true;

    /* Homepage: copy from the page, remember the id for the other pages. */
    var sec = pageBannerSection();
    if (sec) {
      set('localStorage', idKey(), sec.id.replace('shopify-section-', ''));
      var root = sec.querySelector('.galaxy-banner');
      if (!root || !build(root, null, slot)) fail(slot);
      return;
    }

    slot.setAttribute('data-state', 'loading');
    withTimeout(getHtml())
      .then(function (h) {
        var doc = new DOMParser().parseFromString(h, 'text/html');
        var r = doc.querySelector('.galaxy-banner');
        var styles = Array.prototype.slice.call(doc.querySelectorAll('style'));
        if (!r || !build(r, styles, slot)) fail(slot);
      })
      .catch(function () { fail(slot); });
  }

  /* Remember the id whenever the homepage is shown, so later pages skip the lookup. */
  var homeSec = pageBannerSection();
  if (homeSec) set('localStorage', idKey(), homeSec.id.replace('shopify-section-', ''));

  var details = document.getElementById('Details-menu-drawer-container');
  if (!details) return;
  var summary = details.querySelector(':scope > summary');
  if (summary) {
    summary.addEventListener('touchstart', start, { passive: true });
    summary.addEventListener('mousedown', start);
    summary.addEventListener('click', start);
  }
  details.addEventListener('toggle', function () { if (details.open) start(); });
})();
